import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  Fun,
  IOWidget,
  any,
  async,
  fromJSX,
  nothing,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../../subscription/subscriptionState'
import { fieldReplacer, fieldUpdater } from '../../../../utils/updaters'
import { blogTeasersWidget } from '../../blogs/blogTeasers/blogTeasersWidget'
import { collectionEditorWidget } from '../../collections/collectionEditor/collectionEditorWidget'
import { CollectionTeasersLayout } from '../../collections/collectionTeasers/collectionTeasersLayout'
import { CollectionsLoaderState } from '../../collections/collectionsLoader/collectionsLoaderState'
import { componentSaverWidget } from '../../components/componentSaver/componentSaverWidget'
import { componentTeasersWidget } from '../../components/componentTeasers/componentTeasersWidget'
import { dishHighlightWidget } from '../../dishes/dishHighlight/dishHighlightWidget'
import { guestChefHighlightWidget } from '../../guestChef/guestChefHighLight/guestChefHighLight.widget'
import { knowledgeHighlightWidget } from '../../knowledge/knowledgeHighlight/knowledgeHighlightWidget'
import { paywallWidget } from '../../paywall/paywallWidget'
import { BlocksLayout } from '../../sharepointPage/blocks/blocksLayout'
import { HomeData } from '../home.state'
import {
  DishBlogsWrapperLayout,
  HeaderAndSearchBar,
  TextBlock,
} from './loggedInHomeLayout'
import {
  AdditionalSubsectionState,
  ExtendedLoggedInHomeState,
  LoggedInHomeState,
  loggedInHomeStateUpdaters,
} from './loggedInHomeState'

export const loggedInHomeWidget =
  (
    searchString: string,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsLoaderState: CollectionsLoaderState,
    isSignupSuccessBannerShowing: boolean,
    homeData?: HomeData
  ): IOWidget<
    LoggedInHomeState,
    Fun<LoggedInHomeState, ExtendedLoggedInHomeState>
  > =>
  (state) =>
    any([
      async(state.componentOfTheDay).map(
        fieldUpdater<ExtendedLoggedInHomeState>()('componentOfTheDay')
      ),
      fromJSX((setState) => {
        return (
          <HeaderAndSearchBar
            isSignupSuccessBannerShowing={isSignupSuccessBannerShowing}
            firstName={
              userSubscriptionsState.kind == 'loaded'
                ? userSubscriptionsState.value.User.Name
                : ''
            }
            firstNameLoaded={userSubscriptionsState.kind == 'loaded'}
            componentOfTheDay={
              state.componentOfTheDay.kind == 'loaded'
                ? state.componentOfTheDay.value
                : 'loading'
            }
            searchString={searchString}
            setSearchString={(newSearchString) =>
              setState(
                fieldReplacer<ExtendedLoggedInHomeState>()('searchString')(
                  newSearchString
                )
              )
            }
          />
        )
      }),
      paywallWidget(true, userSubscriptionsState)(
        loggedInHomeBaseWidget(collectionsLoaderState)(state),
        loggedInHomeFullWidget(collectionsLoaderState, homeData)(state)
      ),
    ])

const loggedInHomeFullWidget =
  (
    collectionsState: CollectionsLoaderState,
    homeData?: HomeData
  ): IOWidget<
    LoggedInHomeState,
    Fun<LoggedInHomeState, ExtendedLoggedInHomeState>
  > =>
  (state) => {
    const additionalSubsections = state.additionalSubsections
    return any<
      Fun<
        LoggedInHomeState,
        LoggedInHomeState & { reloadCollections?: boolean }
      >
    >([
      // Text block
      fromJSX(() => <TextBlock />),
      async(state.additionalSubsections).map(
        fieldUpdater<ExtendedLoggedInHomeState>()('additionalSubsections')
      ),
      componentSaverWidget(collectionsState)(state.componentSaverState).map(
        loggedInHomeStateUpdaters.componentSaverStateUpdater
      ),
      collectionEditorWidget(
        false,
        undefined
      )(state.collectionCreatorState).map(
        loggedInHomeStateUpdaters.collectionCreatorStateUpdater
      ),
      any<Fun<LoggedInHomeState, ExtendedLoggedInHomeState>>([
        // Newest components
        componentTeasersWidget(
          collectionsState,
          'home-logged-in',
          'link-save',
          {
            title: i18next.t('common:new'),
            readMoreLink:
              i18next.language == 'nl' ? '/nl/componenten' : '/en/components',
          }
        )(state.newestComponents).map(
          loggedInHomeStateUpdaters.newestComponentsUpdater
        ),
        // Last viewed
        componentTeasersWidget(
          collectionsState,
          'home-logged-in',
          'link-save',
          { title: i18next.t('common:recentlyViewed') }
        )(state.lastViewedComponents).map(
          loggedInHomeStateUpdaters.lastViewedComponentsUpdater
        ),
        // Dish & Blogs
        any<Fun<LoggedInHomeState, ExtendedLoggedInHomeState>>([
          homeData == undefined
            ? nothing()
            : dishHighlightWidget(homeData.dishHighlight).map(
                (updater) =>
                  (state): ExtendedLoggedInHomeState => ({
                    ...state,
                    homeDataUpdater:
                      fieldUpdater<HomeData>()('dishHighlight')(updater),
                  })
              ),
          blogTeasersWidget(
            'home-logged-in',
            i18next.t('home:blogsTitle')
          )(state.blogTeasers).map(
            fieldUpdater<LoggedInHomeState>()('blogTeasers')
          ),
        ]).wrapHTML((children) => (
          <DishBlogsWrapperLayout>{children}</DishBlogsWrapperLayout>
        )),
        // Component groups
        additionalSubsections.kind == 'loaded' &&
        additionalSubsections.value.length > 0
          ? additionalSubsectionWidget(collectionsState)(
              additionalSubsections.value[0]
            )
          : nothing(),
        // Guest chef
        homeData == undefined
          ? nothing()
          : guestChefHighlightWidget(collectionsState)(
              homeData.guestChefHighlight
            ).map((updater) => {
              return updater.kind == 'r'
                ? (state): ExtendedLoggedInHomeState => ({
                    ...state,
                    homeDataUpdater: fieldUpdater<HomeData>()(
                      'guestChefHighlight'
                    )(updater.v),
                  })
                : fieldUpdater<ExtendedLoggedInHomeState>()(
                    'componentSaverState'
                  )(updater.v)
            }),
        additionalSubsections.kind == 'loaded' &&
        additionalSubsections.value.length > 1
          ? additionalSubsectionWidget(collectionsState)(
              additionalSubsections.value[1]
            )
          : nothing(),
        // Collection teasers
        fromJSX((setState) => (
          <CollectionTeasersLayout
            kind="home-logged-in"
            title={
              (collectionsState.kind == 'loaded' &&
                collectionsState.value.length > 0) ||
              (collectionsState.kind == 'loading' &&
                collectionsState.value &&
                collectionsState.value.length > 0)
                ? i18next.t('home:collectionsTitle')
                : i18next.t('collection:collectionsTitle')
            }
            collections={
              collectionsState.kind == 'loading'
                ? collectionsState.value == undefined
                  ? 'loading'
                  : collectionsState.value.slice(0, 4)
                : collectionsState.kind == 'loaded'
                ? collectionsState.value.slice(0, 4)
                : 'error'
            }
            onCreate={() =>
              setState(loggedInHomeStateUpdaters.startCreatingUpdater)
            }
            showReadMoreLink={
              (collectionsState.kind == 'loaded' &&
                collectionsState.value.length > 0) ||
              (collectionsState.kind == 'loading' &&
                collectionsState.value &&
                collectionsState.value.length > 0)
            }
          />
        )),
        // Knowledge
        homeData == undefined
          ? nothing()
          : knowledgeHighlightWidget()(homeData.knowledgeHighlight).map(
              (updater) => (state) => ({
                ...state,
                homeDataUpdater:
                  fieldUpdater<HomeData>()('knowledgeHighlight')(updater),
              })
            ),
        // Component groups
        additionalSubsections.kind == 'loaded' &&
        additionalSubsections.value.length > 2
          ? any(
              additionalSubsections.value
                .slice(2)
                .map((section) =>
                  additionalSubsectionWidget(collectionsState)(section)
                )
            )
          : nothing(),
      ]),
    ]).wrapHTML((children) => (
      <BlocksLayout pageType="home-logged-in" children={children} />
    ))
  }

const loggedInHomeBaseWidget =
  (
    collectionsState: CollectionsLoaderState
  ): IOWidget<
    LoggedInHomeState,
    Fun<LoggedInHomeState, ExtendedLoggedInHomeState>
  > =>
  (state) =>
    any<Fun<LoggedInHomeState, ExtendedLoggedInHomeState>>([
      // Text block
      fromJSX(() => <TextBlock />),
      componentTeasersWidget(collectionsState, 'home-logged-in', 'link-only', {
        title: i18next.t('common:new'),
      })(state.newestComponents).map(
        loggedInHomeStateUpdaters.newestComponentsUpdater
      ),
    ]).wrapHTML((children) => (
      <BlocksLayout pageType="home-logged-in" children={children} />
    ))

const additionalSubsectionWidget =
  (collectionsState: CollectionsLoaderState) =>
  (state: AdditionalSubsectionState) =>
    componentTeasersWidget(collectionsState, 'home-logged-in', 'link-save', {
      title: state.Name,
      intro: state.Description,
      readMoreLink: `/${i18next.language}/search?term=${state.Query}`,
    })(state.matchingComponents).map(
      loggedInHomeStateUpdaters.matchingComponentsUpdater(state.Id)
    )
